import { useState, useEffect, ReactNode } from "react";
import { Builder } from "@finbox-in/lending-journey-renderer";

import DialogOne from "../../atomic/Dialog/DialogOne";
import { CollapsibleSidebar } from "../../atomic/CollapsibleSidebar";

import { authService } from "../../../services/apis";
import { getPanelWidth } from "src/services/ApiResponseMiddleware";
import { notify } from "../../../../../src/shared/context/AlertContext";
import { handleCheckStatusResponse } from "../../../shared/utils/Global";
import {
  IBaseUiConfigButtonConfig,
  IUiConfigButtonComponentSize
} from "src/components/atomic/UiConfigButton/types";
import { UIConfigComponentType } from "src/shared/utils/constants";
import {
  getPageName,
  getDocumentID,
  getMediaType
} from "src/shared/utils/Global";
import { CommonService } from "src/services/Common";
import { LoanApplicationService } from "src/services/LoanApplication";
import DetailsComponent from "../UIConfigComponents/DetailsComponent";
import Timeline from "../UIConfigComponents/Timeline";
import UIConfigDetails from "../../../pages/VerificationVKYC/VerificationVKYC";

interface UseBuildPanelOptions {
  title?: string;
  token?: string;
  pageName?: string;
  staticContext?: Record<string, any>;
  onDataRefresh?: () => void;
  activeTab?: any;
  whiteLabelStyle?: any;
  isLenderDashboard?: boolean;
  showPanelPadding?: boolean;
  selectedCardIdName?: string;
}

/**
 * Owns the state and renders the UI for displaying a Builder inside either a
 * CollapsibleSidebar or a DialogOne, depending on the `componentType` returned
 * by UiConfigButton's `onClickFinished` callback.
 *
 * Usage:
 *   const { handleClickFinished, builderPanelNode } = useBuildPanel({ ... });
 *   // pass handleClickFinished as onClickFinished to UiConfigButton
 *   // render builderPanelNode anywhere in JSX
 */
export function useBuildPanel({
  title = "Form",
  token,
  pageName,
  activeTab,
  whiteLabelStyle,
  isLenderDashboard,
  staticContext,
  onDataRefresh,
  showPanelPadding = true,
  selectedCardIdName
}: UseBuildPanelOptions): {
  handleClickFinished: (
    response: any,
    buttonConfig?: IBaseUiConfigButtonConfig
  ) => void;
  builderPanelNode: ReactNode;
} {
  const [currentJson, setCurrentJson] = useState<any>({});
  const [configData, setConfigData] = useState<any>(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [dynamicTitle, setDynamicTitle] = useState<string | undefined>(title);
  const [isSkipColending, setIsSkipColending] = useState(false);
  const [componentSize, setComponentSize] = useState<
    IUiConfigButtonComponentSize | string | undefined
  >(undefined);
  const [configResponse, setConfigResponse] = useState(null);
  const [rendererType, setRendererType] = useState("");
  const [componentName, setComponentName] = useState("");
  const [mediaIDs, setMediaIDs] = useState<string[]>([]);
  const [buttonKey, setButtonKey] = useState("");

  useEffect(() => {
    setDynamicTitle(title);
  }, [title]);

  const handleClickFinished = (
    response: any,
    buttonConfig?: IBaseUiConfigButtonConfig
  ) => {
    const {
      componentType,
      componentSize: sizeFromConfig,
      rendererType,
      componentName
    } = buttonConfig?.component || buttonConfig || {};
    const { label, key } = buttonConfig ?? {};

    if (!componentType && response) {
      handleCheckStatusResponse(response, onDataRefresh ?? (() => {}));
    } else if (componentType) {
      if (
        !rendererType ||
        rendererType === "builder_config_api" ||
        rendererType === "default"
      ) {
        const config = response?.data?.config || {};
        const graphqlResponse =
          response?.data?.graphqlResponse ?? response?.data?.graphQLResponse;
        setCurrentJson(config);
        setConfigData(graphqlResponse);
      } else if (rendererType === "ui_config_v1") {
        setConfigResponse(response);
      }

      if (label) {
        if (label === "Skip Colending") setIsSkipColending(true);

        setDynamicTitle(label);
      }

      setComponentSize(sizeFromConfig);
      setRendererType(rendererType);
      setComponentName(componentName);
      setButtonKey(key);

      if (componentType?.toLowerCase() === UIConfigComponentType.SIDEBAR)
        setShowSidebar(true);
      else setShowDialog(true);
    }
  };

  const handleBuilderSubmit = (data: any) => {
    if (data?.response?.status === 200 || data?.status === 200) {
      notify({
        title: "Success",
        text: "Form submitted successfully",
        type: "success"
      });

      setShowSidebar(false);
      setShowDialog(false);
      onDataRefresh?.();
    } else {
      notify({
        title: "Error",
        text: data?.error?.message || "Something Went Wrong",
        type: "error"
      });
    }
  };

  const uploadLenderMedia = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("mediaType", getMediaType(activeTab));
    formData.append("userID", staticContext?.loanApplicationData?.userID);
    formData.append("documentID", getDocumentID(activeTab, buttonKey));

    const response: any = await CommonService.uploadDashboardMedia({
      token,
      formData: formData as any,
      pageName: getPageName(activeTab),
      isLenderPlatform: isLenderDashboard!
    });

    return {
      status: !!response?.status,
      mediaID: response?.data?.mediaID ?? "",
      error: response?.error ?? ""
    };
  };

  let builderNode;

  if (!rendererType || rendererType === "builder_config_api") {
    builderNode =
      Object.keys(currentJson).length > 0 ? (
        <Builder
          key={JSON.stringify(currentJson)}
          config={currentJson}
          navigate={() => {}}
          onSubmit={handleBuilderSubmit}
          onFileDeleted={(mediaId: string) => {
            setMediaIDs((prev) => {
              const next = (prev || "")
                ?.split?.(",")
                ?.filter((id) => id && id !== mediaId)
                ?.join(",");

              return next || "";
            });
          }}
          onFileUpload={async (file: File) => {
            try {
              const resp = await uploadLenderMedia(file);
              if (resp.status) {
                return {
                  status: !!resp?.status,
                  mediaID: resp?.mediaID ?? resp?.data?.mediaID ?? "",
                  error: resp?.error ?? ""
                };
              }
            } catch (e: any) {
              return {
                status: false,
                mediaID: "",
                error: e?.message ?? "Upload failed"
              };
            }
          }}
          loading={false}
          context={{
            moduleName: "default",
            graphqlResponse: configData,
            ...staticContext
          }}
          apiBasePath={import.meta.env.REACT_APP_JOURNEY_RENDERER_URL}
          apiHeaders={{
            token: token as string,
            "Page-Header": pageName as string,
            authorization: `Bearer ${authService.getBearerToken()}`
          }}
          onDomClick={() => {}}
          isStyleEditor={false}
          reloadConfig={() => {}}
        />
      ) : null;
  } else if (rendererType === "ui_config_v1") {
    builderNode = (
      <UIConfigDetails
        token={token}
        pageName={pageName}
        loanApplicationID={
          staticContext?.loanApplicationData?.loanApplicationID
        }
        configResponse={configResponse}
      />
    );
  } else if (rendererType === "default") {
    switch (componentName) {
      case "Details":
        builderNode = (
          <DetailsComponent
            graphqlResponse={configData}
            token={token}
            whiteLabelStyle={whiteLabelStyle}
            activeTab={activeTab}
            cardIdName={selectedCardIdName as string}
          />
        );
        break;
      case "Timeline":
        builderNode = (
          <Timeline
            token={token}
            isOpen={true}
            workflowInstanceID={configData?.taskList?.[0]?.workflowInstance?.id}
            sourceEntityID={configData?.loanApplication?.sourceEntityID || ""}
            whiteLabelStyle={whiteLabelStyle}
          />
        );
        break;
      default:
        break;
    }
  }

  const builderPanelNode = (
    <>
      {/* Sidebar — rendered when componentType === "Sidebar" */}
      {showSidebar && (
        <CollapsibleSidebar
          show={showSidebar}
          setShow={setShowSidebar}
          customLayout
          title={dynamicTitle}
          style={{
            container: `flex flex-col w-[35vw] min-w-[500px] pb-20 h-[90vh] overflow-scroll ${showPanelPadding ? "p-4" : ""}`
          }}
        >
          {builderNode}
        </CollapsibleSidebar>
      )}

      {showDialog && (
        <DialogOne
          open={showDialog}
          setOpen={setShowDialog}
          title={dynamicTitle}
          styles={{
            panel: getPanelWidth(componentSize),
            panelHeight: isSkipColending ? "h-[40vh]" : "h-[90vh]",
            panelOverflowY: true,
            childContainerOverflowY: true
          }}
        >
          {builderNode}
        </DialogOne>
      )}
    </>
  );

  return { handleClickFinished, builderPanelNode };
}
